/**
 * Global search CMD+K — overlay cross-entity per saltare a:
 *  - Progetti (per nome / cliente)
 *  - Clienti (per nome / P.IVA)
 *  - Preventivi (per nome)
 *  - Materiali (per nome / code)
 *  - Stratigrafie (per nome)
 *
 * Aperto da CMD+K / CTRL+K. Filtra in tempo reale lato client.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from '@/components/ui/command';
import { FolderOpen, UserSquare2, FileText, Package, Layers, Clock, X } from 'lucide-react';

interface SearchableItem {
  id: string;
  type: 'project' | 'customer' | 'estimate' | 'material' | 'stratigraphy';
  label: string;
  secondary?: string;
  to: string;
}

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RECENT_STORAGE_KEY = 'global-search:recent';
const RECENT_MAX = 5;

function getRecentStorageKey(orgId: string | null | undefined): string {
  return `${RECENT_STORAGE_KEY}:${orgId ?? 'anon'}`;
}

function readRecent(orgId: string | null | undefined): SearchableItem[] {
  try {
    const raw = localStorage.getItem(getRecentStorageKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function writeRecent(orgId: string | null | undefined, item: SearchableItem): SearchableItem[] {
  try {
    const current = readRecent(orgId).filter(r => !(r.id === item.id && r.type === item.type));
    const next = [item, ...current].slice(0, RECENT_MAX);
    localStorage.setItem(getRecentStorageKey(orgId), JSON.stringify(next));
    return next;
  } catch {
    return [];
  }
}

function clearRecent(orgId: string | null | undefined): void {
  try {
    localStorage.removeItem(getRecentStorageKey(orgId));
  } catch {
    // ignore
  }
}

const TYPE_ICON: Record<SearchableItem['type'], React.ReactNode> = {
  project: <FolderOpen className="mr-2 h-4 w-4 text-primary" />,
  customer: <UserSquare2 className="mr-2 h-4 w-4 text-primary" />,
  estimate: <FileText className="mr-2 h-4 w-4 text-primary" />,
  material: <Package className="mr-2 h-4 w-4 text-primary" />,
  stratigraphy: <Layers className="mr-2 h-4 w-4 text-primary" />,
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { currentOrganizationId } = useCurrentOrganization();
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<SearchableItem[]>([]);

  // Carica recenti quando il dialog si apre o cambia org
  useEffect(() => {
    if (open) {
      setRecent(readRecent(currentOrganizationId));
      setQuery('');
    }
  }, [open, currentOrganizationId]);

  // Fetch tutte le entità una volta — i numeri sono piccoli per cartongessista
  // tipo (sotto i 1000 records totali). Per i materiali (1100+) limitiamo a 200.
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['global-search', currentOrganizationId, open],
    enabled: open && !!currentOrganizationId,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchableItem[]> => {
      if (!currentOrganizationId) return [];

      const [projectsRes, customersRes, estimatesRes, materialsRes, stratisRes] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any).from('projects').select('id, name, client').eq('organization_id', currentOrganizationId).limit(200),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any).from('customers').select('id, name, vat_number, city').eq('organization_id', currentOrganizationId).limit(200),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any).from('estimates').select('id, name, status').eq('organization_id', currentOrganizationId).limit(200),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any).from('materials_with_pricing').select('id, name, code, category').limit(200),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any).from('stratigraphies').select('id, name, type').eq('organization_id', currentOrganizationId).limit(200),
      ]);

      const out: SearchableItem[] = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (projectsRes.data ?? []).forEach((p: any) => out.push({
        id: p.id, type: 'project', label: p.name, secondary: p.client, to: `/projects/${p.id}`,
      }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (customersRes.data ?? []).forEach((c: any) => out.push({
        id: c.id, type: 'customer', label: c.name,
        secondary: [c.vat_number, c.city].filter(Boolean).join(' · '),
        to: `/customers/${c.id}`,
      }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (estimatesRes.data ?? []).forEach((e: any) => out.push({
        id: e.id, type: 'estimate', label: e.name, secondary: `stato: ${e.status}`,
        to: `/estimates/${e.id}/manage`,
      }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (materialsRes.data ?? []).forEach((m: any) => out.push({
        id: m.id, type: 'material', label: m.name,
        secondary: [m.code, m.category].filter(Boolean).join(' · '),
        to: `/materials`,
      }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (stratisRes.data ?? []).forEach((s: any) => out.push({
        id: s.id, type: 'stratigraphy', label: s.name,
        secondary: s.type,
        // La route del configuratore usa il QUERY param (?id=): il path param
        // /configurator/:id non esiste e portava alla 404.
        to: s.type === 'ceiling' ? `/ceilings/configurator?id=${s.id}` : `/configurator?id=${s.id}`,
      }));
      return out;
    },
  });

  // Raggruppa per type per la visualizzazione
  const grouped = useMemo(() => {
    const map = new Map<SearchableItem['type'], SearchableItem[]>();
    items.forEach(it => {
      const list = map.get(it.type) ?? [];
      list.push(it);
      map.set(it.type, list);
    });
    return map;
  }, [items]);

  const go = (item: SearchableItem) => {
    const next = writeRecent(currentOrganizationId, item);
    setRecent(next);
    onOpenChange(false);
    navigate(item.to);
  };

  const handleClearRecent = () => {
    clearRecent(currentOrganizationId);
    setRecent([]);
  };

  // Mostra i recenti solo se il campo è vuoto.
  const showRecent = !query.trim() && recent.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Cerca progetti, clienti, preventivi, materiali…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {isLoading ? 'Caricamento…' : 'Nessun risultato trovato.'}
        </CommandEmpty>

        {showRecent && (
          <CommandGroup
            heading={
              <div className="flex items-center justify-between gap-2 pr-1">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Recenti
                </span>
                <button
                  type="button"
                  className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                  onClick={handleClearRecent}
                  title="Cancella cronologia"
                >
                  <X className="h-3 w-3" />
                  Pulisci
                </button>
              </div>
            }
          >
            {recent.map(it => (
              <CommandItem
                key={`recent-${it.type}-${it.id}`}
                value={`recenti ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                {TYPE_ICON[it.type]}
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {grouped.get('project') && grouped.get('project')!.length > 0 && (
          <CommandGroup heading="Progetti">
            {grouped.get('project')!.map(it => (
              <CommandItem
                key={it.id}
                value={`projects ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                <FolderOpen className="mr-2 h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {grouped.get('customer') && grouped.get('customer')!.length > 0 && (
          <CommandGroup heading="Clienti">
            {grouped.get('customer')!.map(it => (
              <CommandItem
                key={it.id}
                value={`clienti ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                <UserSquare2 className="mr-2 h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {grouped.get('estimate') && grouped.get('estimate')!.length > 0 && (
          <CommandGroup heading="Preventivi">
            {grouped.get('estimate')!.map(it => (
              <CommandItem
                key={it.id}
                value={`preventivi ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                <FileText className="mr-2 h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {grouped.get('stratigraphy') && grouped.get('stratigraphy')!.length > 0 && (
          <CommandGroup heading="Stratigrafie">
            {grouped.get('stratigraphy')!.map(it => (
              <CommandItem
                key={it.id}
                value={`stratigrafie ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                <Layers className="mr-2 h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {grouped.get('material') && grouped.get('material')!.length > 0 && (
          <CommandGroup heading="Materiali">
            {grouped.get('material')!.map(it => (
              <CommandItem
                key={it.id}
                value={`materiali ${it.label} ${it.secondary ?? ''}`}
                onSelect={() => go(it)}
              >
                <Package className="mr-2 h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{it.label}</div>
                  {it.secondary && <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
